import { FC, useMemo, useEffect } from 'react'

import styled from 'styled-components'
import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form'

import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { DatePicker } from 'src/Components/DatePicker/index.tsx'
import { Offer } from 'src/Redux/api/offersApi'
import { AddClientBody } from 'src/Redux/api/clientsApi'
import { EditClientsModal } from 'src/Pages/Clients/EditClientsModal.tsx'
import { Button } from 'src/Components/Button'

import { ContractFormData } from './useCreateContract'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1200px;
`

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;
`

const FormField = styled.div`
  flex: 1;
  min-width: 200px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  background-color: #f7f9fc;
  border-radius: 8px;
  margin-bottom: 20px;
`

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
`

const InfoLabel = styled.span`
  font-weight: bold;
  color: #333;
  font-size: 14px;
  width: 100px;
  text-align: start;
  white-space: nowrap;
`

const InfoValue = styled.span`
  color: #666;
  font-size: 14px;
`

const WarningSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  margin-top: 10px;
`

const WarningTitle = styled.div`
  font-weight: bold;
  color: #856404;
  font-size: 14px;
  margin-bottom: 5px;
`

const WarningText = styled.div`
  margin: 0;
  color: #856404;
  font-size: 13px;
  line-height: 1.5;
  text-align: start;
`

const EditButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
`

const REPORTING_PERIOD_OPTIONS = [
  {
    label: 'Декада (Каждые 10 дней)',
    value: 'decade',
    description: 'Каждые 10 дней',
  },
  {
    label: 'Полмесяца (с 1 по 15 и с 16 до конца)',
    value: 'half_month',
    description: 'с 1 по 15 число и с 16 до конца месяца',
  },
]

const RESPONSIBILITY_OPTIONS = [
  {
    label: 'их силами за их счет',
    value: 'tenant_tenant',
  },
  {
    label: 'их силами за наш счет',
    value: 'tenant_landlord',
  },
  {
    label: 'нашими силами за их счет',
    value: 'landlord_tenant',
  },
  {
    label: 'нашими силами за наш счет',
    value: 'landlord_landlord',
  },
]

const DELIVERY_TYPE_OPTIONS = [
  {
    label: 'Дата доставки',
    value: 'date',
  },
  {
    label: 'Количество дней для доставки',
    value: 'days',
  },
]

interface ContractFormStepProps {
  control: Control<ContractFormData>
  errors: FieldErrors<ContractFormData>
  offer?: Offer
  onSubmit: () => void
  onCancel: () => void
  setValue: (
    name: keyof ContractFormData,
    value: string | number | Date | null,
  ) => void
  isEditClientModalOpen: boolean
  openEditClientModal: () => void
  closeEditClientModal: () => void
  handleUpdateClient: (data: AddClientBody) => void
}

// Маппинг полей клиента для отображения
const CLIENT_FIELD_LABELS: Record<string, string> = {
  organizationalForm: 'Организационная форма',
  ogrn: 'ОГРН',
  inn: 'ИНН',
  kpp: 'КПП',
  phone: 'Телефон',
  name: 'Название',
  rs: 'Расчетный счет',
  ks: 'Корреспондентский счет',
  bic: 'БИК',
  bankName: 'Название банка',
  email: 'Email',
  legalAddress: 'Юридический адрес',
  postAddress: 'Почтовый адрес',
  headPosition: 'Должность руководителя',
  director: 'ФИО руководителя',
  headNameGenitive: 'ФИО руководителя (род. падеж)',
}

export const ContractFormStep: FC<ContractFormStepProps> = ({
  control,
  errors,
  offer,
  setValue,
  isEditClientModalOpen,
  openEditClientModal,
  closeEditClientModal,
  handleUpdateClient,
}) => {
  const equipmentDeliveryType = useWatch({
    control,
    name: 'equipmentDeliveryType',
    defaultValue: 'days',
  })

  const foodRule = useWatch({
    control,
    name: 'foodRule',
  })

  // Поле "Стоимость питания" активно только если питание их силами (tenant_tenant или tenant_landlord)
  const isFoodCostDisabled =
    foodRule !== 'tenant_tenant' && foodRule !== 'tenant_landlord'

  // Очищаем стоимость питания если поле становится disabled
  useEffect(() => {
    if (isFoodCostDisabled) {
      setValue('dailyFoodCostPerEmployee', null)
    }
  }, [isFoodCostDisabled, setValue])

  // Проверяем незаполненные поля клиента
  const missingClientFields = useMemo(() => {
    if (!offer?.client) return []

    const client = offer.client
    const fieldsToCheck: Array<keyof typeof client> = [
      'organizationalForm',
      'ogrn',
      'inn',
      'kpp',
      'phone',
      'name',
      'rs',
      'ks',
      'bic',
      'bankName',
      'email',
      'legalAddress',
      'postAddress',
      'headPosition',
      'director',
      'headNameGenitive',
    ]

    return fieldsToCheck.filter((field) => {
      const value = client[field]
      return !value || value === ''
    })
  }, [offer?.client])

  return (
    <FormContainer>
      {offer && (
        <InfoSection>
          <InfoRow>
            <InfoLabel>Клиент:</InfoLabel>
            <InfoValue>{offer.client?.name || 'Не указан'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Объект работ:</InfoLabel>
            <InfoValue>{offer.clientDivision || 'Не указан'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Номер КП:</InfoLabel>
            <InfoValue>{offer.offerNumber}</InfoValue>
          </InfoRow>

          {missingClientFields.length > 0 && (
            <WarningSection>
              <WarningTitle>
                Внимание! Не заполнены следующие данные клиента:
              </WarningTitle>
              <WarningText>
                {missingClientFields
                  .map((field) => CLIENT_FIELD_LABELS[field as string] || field)
                  .join(', ')}
              </WarningText>
              <EditButtonContainer>
                <Button onClick={openEditClientModal}>
                  Редактировать данные клиента
                </Button>
              </EditButtonContainer>
            </WarningSection>
          )}
        </InfoSection>
      )}

      <FormRow>
        <FormField>
          <Controller
            name='contractDate'
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                label='Дата заключения договора'
                placeholder='Выберите дату'
                error={errors.contractDate?.message}
              />
            )}
          />
        </FormField>

        <FormField>
          <Controller
            name='endDate'
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                label='Дата окончания договора'
                placeholder='Выберите дату'
                error={errors.endDate?.message}
              />
            )}
          />
        </FormField>

        <FormField>
          <Controller
            name='rentPeriod'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Срок аренды (мес.)'
                placeholder='Количество месяцев'
                type='number'
                error={errors.rentPeriod?.message}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            )}
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Controller
            name='foodRule'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label='Питание'
                placeholder='Выберите вариант'
                data={RESPONSIBILITY_OPTIONS}
                error={errors.foodRule?.message}
              />
            )}
          />
        </FormField>

        <FormField>
          <Controller
            name='equipmentDeliveryType'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label='Условия доставки техники Арендатором'
                placeholder='Выберите тип'
                data={DELIVERY_TYPE_OPTIONS}
                error={errors.equipmentDeliveryType?.message}
              />
            )}
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Controller
            name='dailyFoodCostPerEmployee'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Стоимость питания на 1 сотрудника сут/руб'
                placeholder='Например: 500'
                type='number'
                disabled={isFoodCostDisabled}
                error={errors.dailyFoodCostPerEmployee?.message}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            )}
          />
        </FormField>

        <FormField>
          {equipmentDeliveryType === 'date' ? (
            <Controller
              name='equipmentDeliveryDate'
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label='Дата доставки техники на объект'
                  placeholder='Выберите дату'
                  error={errors.equipmentDeliveryDate?.message}
                  defaultDate={new Date()}
                />
              )}
            />
          ) : (
            <Controller
              name='equipmentDeliveryDays'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  label='Срок доставки техники'
                  placeholder='Количество дней'
                  type='number'
                  error={errors.equipmentDeliveryDays?.message}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          )}
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Controller
            name='accommodationRule'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label='Проживание'
                placeholder='Выберите вариант'
                data={RESPONSIBILITY_OPTIONS}
                error={errors.accommodationRule?.message}
              />
            )}
          />
        </FormField>

        <FormField>
          <Controller
            name='reportingPeriodName'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label='Отчетный период'
                placeholder='Выберите период'
                data={REPORTING_PERIOD_OPTIONS.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
                error={errors.reportingPeriodName?.message}
                onChange={(value) => {
                  field.onChange(value)
                  const option = REPORTING_PERIOD_OPTIONS.find(
                    (opt) => opt.value === value,
                  )
                  if (option) {
                    setValue('reportingPeriodDescription', option.description)
                  }
                }}
              />
            )}
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <Controller
            name='fuelRule'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label='ГСМ'
                placeholder='Выберите вариант'
                data={RESPONSIBILITY_OPTIONS}
                error={errors.fuelRule?.message}
              />
            )}
          />
        </FormField>

        <FormField>
          <Controller
            name='prepaymentNoticeDays'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Количество дней до внесения предоплаты'
                placeholder='Например: 3'
                type='number'
                error={errors.prepaymentNoticeDays?.message}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            )}
          />
        </FormField>
      </FormRow>

      {offer?.client && (
        <EditClientsModal
          isOpened={isEditClientModalOpen}
          onClose={closeEditClientModal}
          onEditClient={handleUpdateClient}
          clientToEdit={offer.client}
        />
      )}
    </FormContainer>
  )
}
